<script context="module" lang="ts">
	import type { SectionConfig } from '$lib/util/pagebuilder';
	import { gql } from 'graphql-request';
	import Media from '$lib/components/media/Media.svelte';
	import { page } from '$app/stores';

	import dayjs from 'dayjs';

	import Section from '$lib/components/layout/Section.svelte';
	import Loader from '$lib/components/util/Loader.svelte';
	import Text from '$lib/components/Text.svelte';
	import TextWithIcon from '$lib/components/util/TextWithIcon.svelte';
	import { getDirectus } from '$lib/util/directus';
	import { onMount } from 'svelte';

	export async function load(data, params) {
		let limit = 5;
		switch (data.display_type) {
			case 'three-cards':
				limit = 3;
				break;
			case 'grid':
				limit = 12;
				break;
		}

		let query = `items/posts
				?sort=-date
				&limit=${limit}
				&fields[]=date,author.*
				&fields[]=author.image.id
				&fields[]=author.image.type
				&fields[]=author.image.translations.*
				&fields[]=categories.post_category_id.translations.name
				&fields[]=categories.post_category_id.translations.languages_code
				&fields[]=translations.*
				&fields[]=preview_image.id
				&fields[]=preview_image.type
				&fields[]=preview_image.translations.*
				&meta=filter_count`;

		if (data.post_types !== 'all') {
			query += '&filter[post_types]=' + data.post_types;
		}

		const [posts, categories] = await Promise.all([
			await getDirectus(query, null, params.lang),
			await getDirectus(`items/post_category?fields=*.*.*,translations.*`, null, params.lang)
		]);

		return {
			posts: posts.data,
			meta: posts.meta,
			categories: categories.data
		};
	}

	export const config: SectionConfig = {
		collection: 'section_post_overview',
		availableFor: ['pages', 'special-pages'],
		load,
		query: gql`
      internal_name
			post_types
			show_filters
			display_type
			background_media {
			...Media
			}
			translations {
				languages_code {
					code
				}
				header
				title
				subtitle
			}
				link {
				...Link}
    `
	};

	// optional: define a type for the props (note: injectTranslations is applied)
	type Props = {
		postList: Array<any>;
		title: string;
		subtitle: string;
		post_types: string;
		show_filters: boolean;
		display_type: string;
		background_media: any;
		link: any;
		header: string;
	};
</script>

<script lang="ts">
	import DirectusLink from '$lib/components/navigation/DirectusLink.svelte';
	import GlobalMedia from '$lib/components/media/GlobalMedia.svelte';

	export let data: Props; // the components gets all data via this prop (additionally each top level prop is directly provided to the component)
	export let additionalData: Record<string, any>; // additional data loaded using the load function
	export let excludePost: number; // (Optional) exclude post with this id from results (usually used to display more posts on the detail page)

	let postList: Array<any> = additionalData.posts;

	let categories: Array<any> = additionalData.categories;
	let postsPerPage: number = 6;
	let totalPages: number = getTotalPages(additionalData.meta?.filter_count ?? 0);
	let currentPage: number = 1;

	let selectedCategory: number | null = null;
	let search: string | null = null;
	let isLoading: boolean = false;

	let debouncer: any;
	let mounted: boolean = false;
	let headerElement: any = null;

	let specialPageSlug: string;
	$: specialPageSlug = getCurrentPrefixSlug($page.params.lang);

	function getCurrentPrefixSlug(currentLang: string) {
		for (const specialPage of $page.data.specialPagesData) {
			if (specialPage.name === 'blog') {
				for (const lang of specialPage.translations) {
					if (lang.languages_code === currentLang) {
						return lang.slug;
					}
				}
			}
		}
		return '';
	}

	function getTotalPages(postCount: number) {
		if (data.display_type == 'three-cards') {
			return 1;
		}

		let newTotalPages = Math.floor(postCount / postsPerPage);
		if (postCount % postsPerPage !== 0) {
			newTotalPages += 1;
		}
		return newTotalPages;
	}

	function changeCategory(catIdx: number | null) {
		selectedCategory = catIdx;
		currentPage = 1;
	}

	function changePage(p: number) {
		currentPage = p;
		scrollTop();
	}

	async function loadCategories() {
		const response = await getDirectus(
			`items/post_category?fields=*,translations.*`,
			null,
			$page.params.lang
		);

		if (response?.data) {
			categories = response.data;
		}
	}

	function scrollTop() {
		setTimeout(() => {
			headerElement.scrollIntoView({ behavior: 'smooth' });
		}, 100);
	}

	async function loadPosts() {
		isLoading = true;

		let query = `items/posts
				?sort=-date
				&limit=${postsPerPage}
				&offset=${(currentPage - 1) * postsPerPage}
				&fields[]=date,author.*
				&fields[]=author.image.id
				&fields[]=author.image.type
				&fields[]=author.image.translations.*
				&fields[]=categories.post_category_id.translations.name
				&fields[]=categories.post_category_id.translations.languages_code
				&fields[]=translations.*
				&fields[]=preview_image.id
				&fields[]=preview_image.type
				&fields[]=preview_image.translations.*
				&meta=filter_count`;

		if (excludePost) {
			query += '&filter[id][_neq]=' + excludePost;
		}

		if (data.post_types === 'featured') {
			query += '&filter[featured]=1';
		}

		if (data.post_types === 'not_featured') {
			query += '&filter[featured]=0';
		}

		if (selectedCategory !== null) {
			query += '&filter[categories][post_category_id][_eq]=' + categories[selectedCategory].id;
		}

		const response = await getDirectus(query, null, $page.params.lang);

		if (response.data) {
			totalPages = getTotalPages(response.meta.filter_count);
			postList = response.data;

			if (data.display_type === 'three-cards') {
				postList = postList.slice(0, 2);
			}
		}

		isLoading = false;
	}

	$: onChange(selectedCategory, currentPage, $page.params.lang);

	onMount(() => {
		mounted = true;
		if (excludePost) {
			loadPosts();
		}
	});

	function onChange(...args) {
		if (mounted) {
			loadPosts();
		}
	}

	const debounce = (v: string) => {
		clearTimeout(debouncer);
		debouncer = setTimeout(() => {
			search = v;
			currentPage = 1;
		}, 750);
	};

	function limitText(text: string) {
		let cutoff = text?.indexOf(' ', 200) ?? '';
		return text?.slice(0, cutoff) ?? '' + '...';
	}

	let isOverflowing = false;

	const checkOverflow = () => {
		const categoriesWrapper = document.querySelector('.categories') as HTMLElement;
		if (categoriesWrapper) {
			isOverflowing = categoriesWrapper.scrollWidth > categoriesWrapper.clientWidth;
		}
	};

	const scrollRight = () => {
		const categoriesWrapper = document.querySelector('.categories') as HTMLElement;
		if (categoriesWrapper) {
			categoriesWrapper.scrollBy({ left: 200, behavior: 'smooth' });
		}
	};

	onMount(() => {
		checkOverflow();
		window.addEventListener('resize', checkOverflow);
		return () => window.removeEventListener('resize', checkOverflow);
	});
</script>

<Section gapType={data.background_media ? 'padding' : 'margin'} class="section-blog-overview">
	<div slot="background" class="section-background">
		<Media {...data.background_media} fit="cover" transform="xl" />
	</div>
	{#if data.header}
		<div class="header-hero">
			{data.header}
		</div>
	{/if}
	{#if data.show_filters}
		<div class="categories-wrapper">
			<div class="categories">
				<div class="category-header">
					{#if data.title}
						{data.title}
					{/if}
				</div>

				<div
					class="category"
					class:active={selectedCategory === null}
					on:click={() => changeCategory(null)}
				>
					<div class="all">
						<Text key="all" />
					</div>
				</div>
				{#each categories as category, i}
					<div
						class="category"
						class:active={selectedCategory === i}
						on:click={() => changeCategory(i)}
					>
						<div class="icon-wrapper">
							<div class="icon">
								<Media {...category.icon} fit="cover" transform="m" />
							</div>
							<div class="hover-icon">
								<Media {...category.hover_icon} fit="cover" transform="m" />
							</div>
						</div>
						<div class="category-name">
							{category.name}
						</div>
					</div>
				{/each}
			</div>
			{#if isOverflowing}
				<div class="scroll-arrow" on:click={scrollRight}>
					<GlobalMedia key="icon_caret_grey" />
				</div>
			{/if}
		</div>
	{/if}
	<div
		class="news-wrapper {data.display_type} {data.background_media &&
		data.display_type === 'three-cards'
			? 'three-cards'
			: 'grid'}"
	>
		<div class="header" bind:this={headerElement}>
			{#if data.link}
				<div class="header-title">
					{#if data.title}
						{data.title}
					{/if}
				</div>
				<div class="header-link">
					<DirectusLink {...data.link}><TextWithIcon {...data.link} /></DirectusLink>
				</div>
			{/if}
		</div>
		{#if isLoading}
			<div class="loader-wrapper">
				<Loader color="#FED671" />
			</div>
		{:else if postList.length === 0}
			<div class="no-results"><Text key="no_results" /></div>
		{:else}
			<div class="post-grid {data.display_type}">
				{#each postList as post, i}
					{#key post.id}
						<a
							href={'/' + $page.params.lang + '/' + specialPageSlug + '/' + post.slug}
							class="post-card animated fade-up started"
							style="animation-delay: {100 * i}ms"
						>
							<div class="image">
								<Media {...post.preview_image} fit="cover" transform="xl" />
							</div>
							<div class="row">
								<div class="date">
									{#if post.date}
										{dayjs(post.date?.replace('T', ' ')).format('DD.MM.YYYY')}
									{/if}
								</div>
								<div class="category-wrapper">
									{#each post.categories ?? [] as { post_category_id: item }, idx}
										<div class="category-title">{item.name}</div>
										{#if idx < post.categories.length - 1}
											<span class="seperator">|</span>
										{/if}
									{/each}
								</div>
							</div>
							<div class="text">
								<div class="title">
									{post.title}
								</div>
								<div class="description-wrapper">
									<div class="description">
										{limitText(post.preview_text)}
									</div>
									<div class="read-more">
										<span class="divider" />
										<div class="read-more-text">
											<Text key="read_more" />
										</div>
									</div>
								</div>
								{#if !data.background_media && !data.show_filters}
									<div class="read-more-link">
										<div class="read-more-text">
											<TextWithIcon textKey="read_more" iconKey="icon-arrow-right-long" />
										</div>
									</div>
								{/if}
							</div>
						</a>
					{/key}
				{/each}
			</div>
		{/if}

		{#if totalPages > 1}
			<div class="pagination">
				{#each { length: totalPages } as _, i}
					<button on:click={() => changePage(i + 1)} class:active={i + 1 === currentPage}>
						{i + 1}
					</button>
				{/each}
			</div>
		{/if}
	</div>
</Section>

<style lang="scss">
	:global(.section-blog-overview) {
		margin-top: v(content-gap-xl) !important;
	}
	:global(.section-blog-overview .inner-section) {
		max-width: 100% !important;
	}
	.section-background {
		height: 100%;
	}
	.news-wrapper {
		background: color(white);
		padding: v(content-padding-xl);
		border-radius: v(border-radius-sm);
		&.grid {
			background: none;
			padding: 0 !important;
			margin-top: v(content-gap);
			.header {
				display: none;
			}
		}
	}
	.header {
		margin-bottom: v(content-padding-xl);
		display: flex;
		align-items: flex-end;
		gap: v(content-gap-lg);
		@include media(phone) {
			gap: v(content-gap-xs);
		}
		:global(a) {
			color: color(black);
			text-transform: uppercase;
			@include font(h6);
		}
		.header-title {
			@include font(h3);
			text-transform: uppercase;
			color: color(black);
		}
		.header-link {
			text-wrap: nowrap;
			display: flex;
			align-items: center;
		}
	}

	.search-wrapper {
		display: flex;
		align-items: center;
		margin-bottom: v(content-padding);

		label {
			margin-inline-end: 8px;
		}
	}
	.loader-wrapper {
		width: 100%;
		min-height: 200px;
		display: flex;
		justify-content: center;
		align-items: center;
	}

	.post-grid {
		display: grid;
		grid-gap: v(content-gap-4xl);
		@media (max-width: 1100px) {
			grid-gap: v(content-gap);
		}

		&.three-cards {
			grid-template-columns: repeat(3, minmax(0, 1fr));
			// grid-template-rows: 1fr;
			@media (max-width: 1100px) {
				grid-template-columns: repeat(1, minmax(0, 1fr));
			}
			.row {
				display: none;
			}
			.post-card {
				> .image {
					width: 100%;
					height: auto;
					position: relative;
					object-fit: cover;
					aspect-ratio: 16 / 9;
					overflow: hidden;
					object-fit: cover;
					@media (max-width: 1100px) {
						aspect-ratio: 24/10;
					}
					@media (max-width: 500px) {
						aspect-ratio: 14/9;
					}
					:global(img) {
						position: absolute;
						width: 100%;
						height: 100%;
						inset-inline-start: 0;
						bottom: 0;
						top: 0;
					}

					@media (max-width: 1024px) {
						height: auto;
					}

					:global(img),
					:global(video) {
						transition: all 0.3s ease;
					}
				}

				&:hover > .image {
					:global(img),
					:global(video) {
						transform: scale(1.06);
					}
				}
			}
		}

		&.grid {
			grid-template-columns: repeat(5, minmax(0, 1fr));
			grid-gap: v(content-gap);
			// grid-template-rows: 1fr auto auto;
			.row {
				display: flex;
				margin-top: v(content-gap-xs);
				color: color(black);
				@include font(text-xs);
				gap: v(content-gap-xs);
				overflow: hidden;
				.category-wrapper {
					display: flex;
					gap: v(content-gap-xs);
					flex-wrap: wrap;

					.category-title {
						white-space: nowrap;
					}
				}
			}

			.post-card {
				background: color(white);
				padding: v(content-padding-xs);
				border-radius: v(border-radius);
				display: grid;
				grid-template-rows: 240px auto auto;
				@media (max-width: 1600px) {
					grid-template-rows: 200px auto auto;
				}
				@media (max-width: 1300px) {
					grid-template-rows: auto;
				}

				> .image {
					width: 100%;
					height: 100%;
					aspect-ratio: 13/10;
					position: relative;
					overflow: hidden;
					object-fit: cover;

					:global(img) {
						position: absolute;
						width: 100%;
						height: 100%;
						inset-inline-start: 0;
						bottom: 0;
					}
					@include media(phone) {
						aspect-ratio: 13/7;
					}
					:global(img),
					:global(video) {
						transition: all 0.3s ease;
					}
					&:hover {
						:global(img),
						:global(video) {
							transform: scale(1.06);
						}
					}
				}
				.description-wrapper {
					display: none;
				}

				.text {
					color: black;
					height: 100%;
					padding-bottom: 0;
					display: flex;
					align-items: flex-end;
				}
			}

			@media (max-width: 1300px) {
				grid-template-columns: repeat(2, minmax(0, 1fr));

				.post-card {
					grid-column: unset !important;
					grid-row: unset !important;

					grid-template-columns: 1fr !important;

					> .image {
						height: auto;
					}
				}
			}

			@media (max-width: 600px) {
				grid-template-columns: repeat(1, minmax(0, 1fr));
			}
		}
	}

	.post-card {
		width: 100%;
		text-decoration: none;
		display: flex;
		flex-direction: column;

		@media (max-width: 1024px) {
			display: flex;
			flex-direction: column;
		}

		.text {
			padding: v(content-padding) 0;
			color: color(black);
			height: 100%;
			flex: 1;

			@include font(text);

			@media (max-width: 1100px) {
				height: auto;
			}
			@include media(phone) {
				padding: v(content-padding-sm) 0;
			}
		}

		.title {
			@include font(h5);
			margin-bottom: v(content-padding-sm);
			@include media(tablet) {
				@include font(h4);
			}
		}
		.description-wrapper {
			display: grid;
			grid-template-columns: 3fr 1fr;
			padding: v(content-gap-xs) 0;
			border-top: solid 1px;
			border-bottom: solid 1px;
			@include media(tablet) {
				grid-template-columns: 2fr 1fr;
			}
		}

		.description {
			@include font(text-sm);
			margin-inline-end: v(content-padding);
			padding: v(content-gap-xs) 0;
			@include media(tablet) {
				padding-inline-end: 0;
			}
		}

		.read-more {
			text-transform: uppercase;
			@include font(h6);
			display: flex;
			justify-content: flex-end;
			align-items: center;
			.read-more-text {
				padding-inline-start: v(content-padding);
				@include media(phone) {
					padding-inline-start: v(content-padding-sm);
				}
			}
		}
	}
	.categories {
		background: color(white);
		border-radius: v(border-radius);
		display: flex;
		padding: v(content-padding-sm) v(content-padding-lg);
		justify-content: space-around;
		overflow: auto;
		width: 100%;

		::-webkit-scrollbar {
			display: none;
		}
		-ms-overflow-style: none; /* IE and Edge */
		scrollbar-width: none; /* Firefox */
		@media (max-width: 1100px) {
			justify-content: normal;
		}
	}
	.category {
		display: flex;
		position: relative;
		align-items: center;
		gap: v(content-gap-sm);
		@include font(h5-collection);
		cursor: pointer;
		flex-shrink: 0;

		&:hover,
		&.active {
			.category-name,
			.all {
				text-decoration: underline;
			}
			.icon {
				opacity: 0;
			}
			.hover-icon {
				opacity: 1;
			}
		}
	}
	.category-header {
		@include font(h4);
		// max-width: 12ch;
		width: 12ch;
		white-space: initial;
		display: flex;
		align-items: center;
	}
	.category-name {
		max-width: 10ch;
		text-wrap: wrap;
	}
	.icon-wrapper {
		position: relative;
		width: 2rem;
		height: 2rem;
	}
	.icon,
	.hover-icon {
		width: 2rem;
		height: 2rem;
		transition: opacity 0.3s ease;
		position: absolute;
		top: 0;
		inset-inline-start: 0;
	}
	.icon {
		opacity: 1;
	}
	.hover-icon {
		opacity: 0;
	}

	.divider {
		width: 1px;
		height: 100%;
		background: color(black);
	}
	@media (max-width: 1100px) {
		.icon-wrapper {
			display: none;
		}
		.categories {
			inset-inline-start: 0;
			width: 100%;
			gap: v(content-gap-sm);
		}

		.category-name,
		.all {
			border: solid 1px color(black);
			border-radius: v(border-radius);
			padding: v(content-padding-xs);
			transition: all 0.3s ease;
			max-width: 100%;
			text-wrap: nowrap;
		}
		.category {
			cursor: pointer;
			&:hover,
			&.active {
				.category-name,
				.all {
					text-decoration: none;
					background: color(primary);
					border: solid 1px transparent;
				}
			}
		}
	}
	.read-more-link {
		padding-top: v(content-padding-sm);
	}

	.scroll-arrow {
		display: flex;
		background: transparent;
		// position: absolute;
		inset-inline-end: 0;
		z-index: 1;
		width: 14px;
		height: 14px;
	}
	.categories-wrapper {
		display: flex;
		align-items: center;
		gap: v(content-gap-xs);
	}
	.header-hero {
		@include font(h1);
		margin: v(content-gap-xl) 0;
		@include media(phone) {
			margin: v(content-gap) 0;
		}
	}
	.pagination {
		display: none;
	}
</style>
